/**
 * How a grid is colored by an attribute that has no scale of its own ("Color
 * by" anything but a rank or score): each cell takes the band its value's
 * percentile rank falls in. Shared by the live grids (GridCellLayer), the
 * historical POST grids (WeatherEventsLayer) and the legends describing them.
 */
import type { RGBAColor } from './deckgl-types';

/** The ramp, top band first. */
export const PERCENTILE_COLOR_RANGE: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

/** Upper bound of each band but the top, bottom band first. */
const BAND_BOUNDARIES = [0.2, 0.4, 0.6, 0.8];

/** Legend rows for a grid colored this way, top band first. */
export const PERCENTILE_LEGEND_ITEMS = [
  'Top 20%',
  '60% - 80%',
  '40% - 60%',
  '20% - 40%',
  'Bottom 20%',
].map((label, index) => {
  const [r, g, b] = PERCENTILE_COLOR_RANGE[index];
  return { label, color: `rgb(${r}, ${g}, ${b})` };
});

/**
 * The percentile rank of every distinct value: the share of values below it,
 * so the smallest value ranks 0 and tied values share the rank of their first
 * occurrence.
 */
export function percentileRanks(values: number[]): Map<number, number> {
  const sorted = [...values].sort((a, b) => a - b);
  const ranks = new Map<number, number>();
  sorted.forEach((value, index) => {
    if (!ranks.has(value)) {
      ranks.set(value, index / sorted.length);
    }
  });
  return ranks;
}

/** Names the group a cell ranks within, from the cell's properties. */
export type RankGroupOf = (properties: Record<string, unknown> | null | undefined) => unknown;

/**
 * Percentile ranks computed separately within each group, keyed by group,
 * for a grid whose cells belong to several events: a cell is ranked against
 * the other cells of its own event, as POST priority is, so a small event
 * still spans the bands instead of sitting in the bottom of a national one.
 */
export function percentileRanksByGroup(
  cells: { group: unknown; value: number }[]
): Map<unknown, Map<number, number>> {
  const valuesByGroup = new Map<unknown, number[]>();
  for (const { group, value } of cells) {
    const values = valuesByGroup.get(group);
    if (values == null) {
      valuesByGroup.set(group, [value]);
    } else {
      values.push(value);
    }
  }
  return new Map(
    Array.from(valuesByGroup, ([group, values]) => [group, percentileRanks(values)] as const)
  );
}

const NOT_DRAWN: RGBAColor = [0, 0, 0, 0];

/** The ramp color for a percentile rank; a value with no rank is not drawn. */
export function percentileBandColor(rank: number | undefined): RGBAColor {
  if (rank == null) {
    return NOT_DRAWN;
  }
  const band = BAND_BOUNDARIES.findIndex(boundary => rank < boundary);
  return PERCENTILE_COLOR_RANGE[band === -1 ? 0 : PERCENTILE_COLOR_RANGE.length - 1 - band];
}
